import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, eur, PAY_METHODS, PAY_STATUS, fmtDate } from "../helpers";
import { CreditCard, Wallet, TriangleAlert, Loader2, RotateCcw, Ban, Settings2 } from "lucide-react";
import { Field } from "../components/FormField";
import PaymentDetailDialog from "../components/PaymentDetailDialog";

const ADMIN_ROLES = ["super_admin", "admin", "amministratore"];
const REFUND_METHODS = ["Stripe", "SumUp", "bonifico", "contanti", "altro"];
const emptyRefund = () => ({ amount: "", method: "bonifico", refund_date: new Date().toISOString().slice(0, 10), reason: "", internal_notes: "", linked_transaction_session_id: "" });

export default function Payments() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [apts, setApts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [checking, setChecking] = useState(false);
  const [txs, setTxs] = useState([]);
  const [refundAmt, setRefundAmt] = useState({});
  const [refunds, setRefunds] = useState([]);
  const [rForm, setRForm] = useState(emptyRefund());
  const [rSaving, setRSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBid, setDetailBid] = useState(null);

  const openDetail = (b) => { setDetailBid(b.id); setDetailOpen(true); };

  const load = useCallback(() => api.get("/bookings").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); api.get("/apartments/lookup").then((r) => setApts(r.data)); }, [load]);

  const aptName = (id) => apts.find((a) => a.id === id)?.name || "—";
  const balance = (b) => Math.max((b.total_amount || 0) - (b.paid_amount || 0), 0);
  const optLabel = (s) => t({ pagato: "paid", parziale: "partial", in_sospeso: "pending" }[s] || s);

  const totals = items.reduce((acc, b) => {
    acc.total += b.total_amount || 0; acc.paid += b.paid_amount || 0; acc.due += balance(b); return acc;
  }, { total: 0, paid: 0, due: 0 });

  // ---- Poll status on return from Stripe ----
  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    const MAX = 8;
    if (attempt >= MAX) { setChecking(false); toast.warning("Verifica del pagamento non conclusa. Riprova tra poco."); return; }
    try {
      const r = await api.get(`/payments/session/${sessionId}/status`);
      const ps = r.data?.status;
      if (ps === "paid") { setChecking(false); toast.success("Pagamento completato con successo"); load(); return; }
      if (ps === "expired") { setChecking(false); toast.error("Sessione di pagamento scaduta"); return; }
      if (ps === "failed") { setChecking(false); toast.error("Pagamento non riuscito"); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setChecking(false); toast.error("Errore nella verifica del pagamento");
    }
  }, [load]);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) {
      setChecking(true);
      window.history.replaceState({}, "", window.location.pathname);
      pollStatus(sid);
    }
  }, [pollStatus]);

  // ---- Edit dialog + transactions ----
  const openDialog = async (b) => {
    setForm(b); setTxs([]); setRefundAmt({}); setRefunds([]); setRForm(emptyRefund()); setOpen(true);
    if (isAdmin) {
      try { const r = await api.get(`/payments/transactions/${b.id}`); setTxs(r.data); } catch { /* noop */ }
      try { const rr = await api.get(`/bookings/${b.id}/refunds`); setRefunds(rr.data); } catch { /* noop */ }
    }
  };

  const loadRefunds = async (bid) => {
    try { const rr = await api.get(`/bookings/${bid}/refunds`); setRefunds(rr.data); } catch { /* noop */ }
  };
  const createRefund = async () => {
    const amount = Number(rForm.amount);
    if (!(amount > 0)) { toast.error("Inserisci un importo valido"); return; }
    setRSaving(true);
    try {
      await api.post(`/bookings/${form.id}/refunds`, { ...rForm, amount });
      toast.success("Rimborso registrato");
      setRForm(emptyRefund());
      await loadRefunds(form.id); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel rimborso"); }
    finally { setRSaving(false); }
  };
  const voidRefund = async (rid) => {
    const reason = window.prompt("Motivo dell'annullamento del rimborso:");
    if (reason === null) return;
    try {
      await api.post(`/refunds/${rid}/void`, { reason });
      toast.success("Rimborso annullato");
      await loadRefunds(form.id); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const save = async () => {
    try { await api.put(`/bookings/${form.id}`, form); toast.success(t("saved")); setOpen(false); load(); }
    catch { toast.error("Error"); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doRefund = async (tx) => {
    const paid = Number(tx.amount || 0);
    const already = Number(tx.refunded_amount || 0);
    const refundable = Math.max(paid - already, 0);
    const raw = refundAmt[tx.session_id];
    const amount = raw ? Number(raw) : refundable;
    if (!(amount > 0) || amount > refundable + 0.01) { toast.error("Importo rimborso non valido"); return; }
    try {
      await api.post(`/payments/refund/${tx.session_id}`, { amount });
      toast.success("Rimborso effettuato");
      const r = await api.get(`/payments/transactions/${form.id}`); setTxs(r.data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel rimborso"); }
  };

  const txStatusLabel = (s) => ({ paid: "Pagato", pending: "In attesa", initiated: "Avviato",
    expired: "Scaduto", failed: "Fallito", refunded: "Rimborsato", partially_refunded: "Rimborso parziale" }[s] || s);

  const cards = [
    { icon: Wallet, label: t("totalRevenue"), value: eur(totals.total), tone: "bg-primary text-primary-foreground" },
    { icon: CreditCard, label: t("paidAmount"), value: eur(totals.paid), tone: "bg-emerald-100 text-emerald-700" },
    { icon: TriangleAlert, label: t("balance"), value: eur(totals.due), tone: "bg-rose-100 text-rose-700" },
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="payments-page">
      {checking && (
        <div data-testid="payment-checking-banner" className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Loader2 className="animate-spin" size={16} /> Verifica del pagamento in corso…
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {cards.map((c) => (
          <Card key={c.label} className="flex items-center gap-4 border-slate-200 p-5">
            <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${c.tone}`}><c.icon size={20} /></div>
            <div><div className="text-2xl font-semibold text-slate-900 font-head">{c.value}</div><div className="text-xs text-slate-500">{c.label}</div></div>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">{t("guestName")}</th>
                <th className="px-4 py-3">{t("apartment")}</th>
                <th className="px-4 py-3">{t("totalAmount")}</th>
                <th className="px-4 py-3">{t("paidAmount")}</th>
                <th className="px-4 py-3">{t("balance")}</th>
                <th className="px-4 py-3">{t("paymentMethod")}</th>
                <th className="px-4 py-3">{t("paymentStatus")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} data-testid={`payment-row-${b.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{b.guest_name}</td>
                  <td className="px-4 py-3 text-slate-600">{aptName(b.apartment_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{eur(b.total_amount)}</td>
                  <td className="px-4 py-3 text-emerald-700">{eur(b.paid_amount)}</td>
                  <td className="px-4 py-3 font-medium text-rose-600">{eur(balance(b))}</td>
                  <td className="px-4 py-3 text-slate-600">{b.payment_method}</td>
                  <td className="px-4 py-3"><StatusBadge value={b.payment_status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button data-testid={`manage-payment-${b.id}`} size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => openDetail(b)}>
                        <Settings2 size={14} className="mr-1" />Gestisci
                      </Button>
                      <Button data-testid={`register-payment-${b.id}`} variant="outline" size="sm" onClick={() => openDialog(b)}>{t("edit")}</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">{t("noData")}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{t("payments")} · {form?.guest_name}</DialogTitle><DialogDescription className="sr-only">Form</DialogDescription></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("totalAmount")}><Input type="number" value={form.total_amount} onChange={(e) => set("total_amount", +e.target.value)} /></Field>
              <Field label={t("paidAmount")}><Input data-testid="payment-paid" type="number" value={form.paid_amount} onChange={(e) => set("paid_amount", +e.target.value)} /></Field>
              <Field label={t("paymentMethod")}>
                <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
                  <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("paymentStatus")}>
                <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)}>
                  <SelectTrigger data-testid="payment-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAY_STATUS.map((m) => <SelectItem key={m} value={m}>{optLabel(m)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {isAdmin && (
            <div className="mt-2 border-t border-slate-200 pt-3" data-testid="tx-history">
              <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Transazioni Stripe</div>
              {txs.length === 0 && <div className="text-sm text-slate-400">Nessuna transazione Stripe.</div>}
              <div className="space-y-2">
                {txs.map((tx) => {
                  const refundable = Math.max(Number(tx.amount || 0) - Number(tx.refunded_amount || 0), 0);
                  const canRefund = ["paid", "partially_refunded"].includes(tx.payment_status) && refundable > 0;
                  return (
                    <div key={tx.session_id} data-testid={`tx-${tx.session_id}`} className="rounded-lg border border-slate-200 p-3 text-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-slate-800">{eur(tx.amount)} <StatusBadge value={txStatusLabel(tx.payment_status)} /></div>
                          <div className="text-xs text-slate-400">{fmtDate(tx.created_at)}{Number(tx.refunded_amount) > 0 ? ` · Rimborsato ${eur(tx.refunded_amount)}` : ""}</div>
                        </div>
                        {canRefund && (
                          <div className="flex items-center gap-2">
                            <Input type="number" placeholder={String(refundable)} className="h-8 w-24"
                              data-testid={`refund-amount-${tx.session_id}`}
                              value={refundAmt[tx.session_id] || ""} onChange={(e) => setRefundAmt((s) => ({ ...s, [tx.session_id]: e.target.value }))} />
                            <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50"
                              data-testid={`refund-btn-${tx.session_id}`} onClick={() => doRefund(tx)}>
                              <RotateCcw size={14} className="mr-1" />Rimborsa
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="mt-2 border-t border-slate-200 pt-3" data-testid="refunds-section">
              <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Rimborsi cliente</div>
              <div className="space-y-2">
                {refunds.length === 0 && <div className="text-sm text-slate-400">Nessun rimborso registrato.</div>}
                {refunds.map((r) => (
                  <div key={r.id} data-testid={`refund-item-${r.id}`} className={`rounded-lg border p-3 text-sm ${r.status === "annullato" ? "border-slate-200 bg-slate-50 opacity-70" : "border-rose-200 bg-rose-50/40"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="font-medium text-slate-800">
                          {eur(r.amount)} · <span className="capitalize">{r.type}</span> · {r.method}
                          {r.status === "annullato" && <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">ANNULLATO</span>}
                        </div>
                        <div className="text-xs text-slate-500">{fmtDate(r.refund_date)} · {r.reason || "—"}{r.created_by ? ` · ${r.created_by}` : ""}</div>
                        {r.internal_notes && <div className="mt-0.5 text-xs text-slate-400">Note: {r.internal_notes}</div>}
                      </div>
                      {r.status !== "annullato" && (
                        <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-100"
                          data-testid={`void-refund-${r.id}`} onClick={() => voidRefund(r.id)}>
                          <Ban size={13} className="mr-1" />Annulla
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* New refund form */}
              <div className="mt-3 rounded-lg bg-secondary/40 p-3" data-testid="new-refund-form">
                <div className="mb-2 text-xs font-semibold text-slate-600">Registra nuovo rimborso</div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Importo €"><Input type="number" data-testid="refund-new-amount" value={rForm.amount} onChange={(e) => setRForm((f) => ({ ...f, amount: e.target.value }))} /></Field>
                  <Field label="Data"><Input type="date" data-testid="refund-new-date" value={rForm.refund_date} onChange={(e) => setRForm((f) => ({ ...f, refund_date: e.target.value }))} /></Field>
                  <Field label="Metodo">
                    <Select value={rForm.method} onValueChange={(v) => setRForm((f) => ({ ...f, method: v }))}>
                      <SelectTrigger data-testid="refund-new-method"><SelectValue /></SelectTrigger>
                      <SelectContent>{REFUND_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  {txs.length > 0 && (
                    <Field label="Collega transazione">
                      <Select value={rForm.linked_transaction_session_id || "none"} onValueChange={(v) => setRForm((f) => ({ ...f, linked_transaction_session_id: v === "none" ? "" : v }))}>
                        <SelectTrigger data-testid="refund-new-link"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuna</SelectItem>
                          {txs.filter((tx) => tx.payment_status === "paid" || tx.payment_status === "partially_refunded").map((tx) => (
                            <SelectItem key={tx.session_id} value={tx.session_id}>{eur(tx.amount)} · {fmtDate(tx.created_at)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  <Field label="Motivo" className="col-span-2"><Input data-testid="refund-new-reason" value={rForm.reason} onChange={(e) => setRForm((f) => ({ ...f, reason: e.target.value }))} /></Field>
                  <Field label="Note interne" className="col-span-2"><Textarea data-testid="refund-new-notes" value={rForm.internal_notes} onChange={(e) => setRForm((f) => ({ ...f, internal_notes: e.target.value }))} /></Field>
                </div>
                <Button size="sm" className="mt-2 bg-rose-600 hover:bg-rose-700" data-testid="create-refund-btn" disabled={rSaving} onClick={createRefund}>
                  <RotateCcw size={14} className="mr-1" />{rSaving ? "Salvataggio…" : "Registra rimborso"}
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-payment-btn" onClick={save} className="bg-primary hover:bg-primary/90">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaymentDetailDialog bookingId={detailBid} open={detailOpen} onOpenChange={setDetailOpen} onChanged={load} />
    </div>
  );
}
